export const ADMIN_EMAIL_ENDPOINTS = {
	CONTROLLER: "api/AdminEmail",
	SEND_BULK: "/send-bulk",
	GET_USERS: "/users",
	COUNTRY_RECIPIENT_COUNTS: "/country-recipient-counts",
};

export const USER_NOTIFICATION_ENDPOINTS = {
	/**
	 * The base URL for the user notification API.
	 */
	CONTROLLER: "api/UserNotification",

	/**
	 * Sets the notification preference for a user.
	 * @method PUT
	 * @param {string} userId - The user ID.
	 * @param {NotificationPreferenceType} type - The preference type.
	 * @param {boolean} enabled - Whether notifications are enabled.
	 * @body { userId: string, type: NotificationPreferenceType, enabled: boolean }
	 * @returns { message: string }
	 */
	SET_NOTIFICATION_PREFERENCE: "/set-notification-preference",

	/**
	 * Updates multiple notification preferences for a user.
	 * @method POST
	 * @body { userId: string, preferences: UserNotificationPreferenceDto[] }
	 * @returns { message: string }
	 */
	UPDATE_NOTIFICATION_PREFERENCES: "/update-notification-preferences",

	/**
	 * Gets the notification preference status for a user.
	 * @method GET
	 * @param {string} userId - The user ID.
	 * @param {NotificationPreferenceType} type - The preference type.
	 * @returns { userId: string, type: NotificationPreferenceType, isEnabled: boolean }
	 */
	GET_NOTIFICATION_PREFERENCE_STATUS: "/get-notification-preference-status/:userId/:type",

	/**
	 * Unsubscribes a user from all non-critical notifications using a token.
	 * @method POST
	 * @param {string} token - The unsubscribe token.
	 * @returns { message: string }
	 */
	UNSUBSCRIBE_ALL: "/unsubscribe-all/:token",
};
export const JOB_NOTES_ENDPOINTS = {
	/**
	 * The base URL for the job notes API.
	 */
	CONTROLLER: "api/JobNotes",

	/**
	 * Adds a note to a job.
	 * @param {string} jobId - The job ID.
	 * @param {JobNotesDTO} noteDto - The note data.
	 */
	ADD_NOTE: "/job/:jobId",

	/**
	 * Gets all notes for a job.
	 * @param {string} jobId - The job ID.
	 */
	GET_NOTES_BY_JOB_ID: "/job/:jobId",

	/**
	 * Gets a note by its ID.
	 * @param {string} id - The note ID.
	 */
	GET_NOTE_BY_ID: "/:id",

	/**
	 * Updates a note by its ID.
	 * @param {string} id - The note ID.
	 * @param {JobNotesDTO} noteDto - The note data.
	 */
	UPDATE_NOTE: "/:id",

	/**
	 * Deletes a note by its ID.
	 * @param {string} id - The note ID.
	 */
	DELETE_NOTE: "/:id",

	/**
	 * Gets active reminders for a user.
	 * @param {string} userId - The user ID.
	 */
	GET_ACTIVE_REMINDERS: "/reminders/:userId",

	/**
	 * Marks a reminder as completed.
	 * @param {string} id - The note/reminder ID.
	 */
	MARK_REMINDER_COMPLETED: "/reminder/:id/complete",
};
export const COMPANY_STATUS_ENDPOINTS = {
	/**
	 * The base URL for the company status API.
	 */
	CONTROLLER: "api",

	/**
	 * Fetches the company status by name.
	 * @param {string} name - The name of the company. [From Query]
	 */
	GET_COMPANY_STATUS_BY_NAME: `/CompanyNameStatus`,
	GET_COMPANY_STATUS_BY_NAME_ANONYMOUS: `/CompanyNameStatusAnonymous`,
	GET_COMPANY_STATUS_BY_SLUG: `/companyStatus/slug`,

	/**
	 * Fetches all company statuses.
	 */
	GET_ALL_COMPANY_STATUSES: `/companyStatus`,

	/**
	 * Fetches filtered company statuses.
	 * @param {Object} queryParams - Filter parameters
	 */
	GET_FILTERED_COMPANY_STATUS: `/companyStatus/filter`,

	/**
	 * Fetches lightweight company sitemap entries.
	 */
	GET_COMPANY_SITEMAP_ITEMS: `/companyStatus/sitemap`,

	/**
	 * Fetches company locations statistics.
	 */
	GET_COMPANY_LOCATIONS: `/companyStatus/locations`,

	/**
	 * Fetches company status statistics.
	 */
	GET_COMPANY_STATUS_STATS: `/companyStatus/stats`,

	/**
	 * Fetches all distinct statuses.
	 */
	GET_DISTINCT_STATUSES: `/companyStatus/distinctStatuses`,

	/**
	 * Fetches all distinct locations.
	 */
	GET_DISTINCT_LOCATIONS: `/companyStatus/distinctLocations`,

	/**
	 * Fetches status count.
	 */
	GET_STATUS_COUNT: `/companyStatus/statusCount`,

	/**
	 * Fetches location count.
	 */
	GET_LOCATION_COUNT: `/companyStatus/locationCount`,

	/**
	 * Fetches companies with favicons (Admin only).
	 * @param {Object} queryParams - Optional pagination and filtering parameters
	 * @returns With pagination: {Companies, CurrentPage, PageSize, TotalCount, TotalPages, HasPrevious, HasNext}
	 * @returns Without pagination: Company[]
	 */
	GET_COMPANIES_WITH_FAVICON: `/company`,

	/**
	 * Fetches paginated companies with favicons (Admin only).
	 * @param {Object} queryParams - Required pagination and filtering parameters
	 * @returns {Companies, CurrentPage, PageSize, TotalCount, TotalPages, HasPrevious, HasNext}
	 */
	GET_COMPANIES_WITH_FAVICON_PAGINATED: `/company/paginated`,

	/**
	 * Sets company names with favicons (Admin only).
	 */
	SET_COMPANIES_WITH_FAVICON: `/company`,

	/**
	 * Updates a company by ID (Admin only).
	 * @param {string} id - Company ID
	 */
	UPDATE_COMPANY: `/company/:id`,
	BACKFILL_COMPANY_SLUGS: `/company/slugs/backfill`,

	/**
	 * Deletes a company (Admin only).
	 * @param {string} id - Company ID [Query parameter]
	 */
	DELETE_COMPANY: `/company`,

	/**
	 * Merges companies (Admin only).
	 * @method POST
	 */
	MERGE_COMPANIES: `/company/merge`,
};

export const COMPANY_COMMUNITY_ENDPOINTS = {
	CONTROLLER: "api/company-community",
	GET_ANONYMOUS_FEED: "/feed",
	GET_ANONYMOUS_COMPANY_FEED: "/feed/:companyName",
	GET_DISCUSSION_SITEMAP_ITEMS: "/sitemap",
	GET_PUBLIC_POSTS: "/",
	GET_POSTS: "/:companyId",
	CREATE_POST: "/:companyId",
	UPDATE_POST: "/posts/:postId",
	ADD_COMMENT: "/posts/:postId/comments",
	REPORT_COMMENT: "/comments/:commentId/report",
	DELETE_COMMENT: "/comments/:commentId",
	TOGGLE_LIKE: "/posts/:postId/likes/toggle",
	REPORT_POST: "/posts/:postId/report",
	DELETE_POST: "/posts/:postId",
	GET_REPORTED_POSTS: "/admin/reports",
	MODERATE_POST: "/posts/:postId/moderation",
};
export const NOTIFICATION_ENDPOINTS = {
	/**
	 * The base URL for the notifications API.
	 */
	CONTROLLER: "api/notifications",

	/**
	 * Gets all notifications for the current user.
	 * @method GET
	 * @returns NotificationEntity[]
	 */
	GET_USER_NOTIFICATIONS: `/`,

	/**
	 * Marks a notification as read by ID.
	 * @method PUT
	 * @param {string} notificationId - The notification ID.
	 * @returns void
	 */
	MARK_AS_READ: `/:notificationId/read`,

	/**
	 * Deletes a notification by ID.
	 * @method DELETE
	 * @param {string} notificationId - The notification ID.
	 * @returns void
	 */
	DELETE_NOTIFICATION: `/:notificationId`,

	/**
	 * Creates a new notification.
	 * @method POST
	 * @body NotificationEntity
	 * @returns NotificationEntity
	 */
	CREATE_NOTIFICATION: `/`,

	/**
	 * Marks all notifications as read for the current user.
	 * @method POST
	 * @returns void
	 */
	MARK_ALL_AS_READ: `/mark-all-read`,
};

export const CACHE_ENDPOINTS = {
	/**
	 * The base URL for the cache API (Admin only).
	 */
	CONTROLLER: "api/Cache",

	/**
	 * Refreshes the course cache (Admin only).
	 * Method: POST
	 * @returns { message: string }
	 */
	REFRESH_COURSES_CACHE: "/refresh-courses",
};

export const JOB_STATUS_ENDPOINTS = {
	/**
	 * The base URL for the job status API.
	 */
	CONTROLLER: "api/JobStatusUpdates",

	/**
	 * Fetches all job status updates for a job.
	 * @param {string} jobId - The job ID.
	 */
	GET_ALL_JOB_STATUS_UPDATES: `/:jobId`,
};

export const COUNTRY_ENDPOINTS = {
	/**
	 * The base URL for the country API.
	 */
	CONTROLLER: "api/UserCountries",

	/**
	 * Fetches all countries.
	 */
	GET_ALL_COUNTRIES: `/`,
};

export const COURSE_ENDPOINTS = {
	/**
	 * The base URL for the course API.
	 */
	CONTROLLER: "api/PublicCourse",

	/**
	 * Fetches all courses.
	 */
	GET_ALL_COURSES: `/`,

	/**
	 * Fetches a course by ID.
	 * @param {string} id - The course ID.
	 */
	GET_COURSE_BY_ID: `/:id`,

	/**
	 * Fetches all universities.
	 * @param {string} [params] - Optional query parameters.
	 */
	GET_ALL_UNIVERSITIES: `/universities`,

	/**
	 * Fetches university data by url
	 * @param {string} url - The university URL.
	 */
	GET_UNIVERSITY_BY_URL: `/university/:url`,

	/**
	 * Fetches all shortlisted courses by user ID.
	 * @param {string} userId - The user ID.
	 */
	GET_SHORTLISTED_COURSES: `/users/:userId/shortlists`,

	/**
	 * Adds course to shortlist.
	 * @param {string} id - The course ID.
	 * @param {string} userId - The user ID.
	 */
	ADD_COURSE_TO_SHORTLIST: `/users/:userId/add-shortlist/:id`,

	/**
	 * Removes course from shortlist.
	 * @param {string} id - The course ID.
	 * @param {string} userId - The user ID.
	 */
	REMOVE_COURSE_FROM_SHORTLIST: `/users/:userId/remove-shortlist/:id`,

	/**
	 * Fetches values for dropdowns.
	 */
	GET_COURSE_DROPDOWNS: `/dropdown`,

	GET_ALL_COURSE_URLS: `/courseUrls`,

	GET_ALL_UNIVERSITY_URLS: `/universityUrls`,

	GET_META_DATA: `/courseTypeMetadata`,
};

export const PUBLIC_DASHBOARD_ENDPOINTS = {
	/**
	 * The base URL for the public dashboard API.
	 */
	CONTROLLER: "api/public/PublicDashboard",

	/**
	 * Fetches the public dashboard metrics.
	 */
	GET_PUBLIC_DASHBOARD_METRICS: `/metrics`,

	/**
	 * Fetches the top sources.
	 */
	GET_TOP_SOURCES: `/top-sources`,

	/**
	 * Fetches the time to hire.
	 */
	GET_TIME_TO_HIRE: `/time-to-hire`,
};

export const DASHBOARD_ENDPOINTS = {
	/**
	 * The base URL for the dashboard API.
	 */
	CONTROLLER: "api/users",

	/**
	 * Fetches the dashboard metrics.
	 * @param {string} userId - The user ID.
	 */
	GET_DASHBOARD_METRICS: `/:userId/Dashboard/metrics`,
	GET_UPCOMING_REMINDERS: `/:userId/Dashboard/upcoming-reminders`,

	GET_RESUME_METRICS: `/:userId/Dashboard/resumes`,
	GET_RESUME_PERFORMANCE: `/:userId/Dashboard/resume-performance`,
	GET_APPLICATION_SUMMARY: `/:userId/Dashboard/summary`,
	GET_FUNNEL_METRICS: `/:userId/Dashboard/funnel`,
	GET_APPLICATION_INSIGHTS: `/:userId/Dashboard/insights`,
	GET_ACTIVITY_TRENDS: `/:userId/Dashboard/trends`,

	/**
	 * Fetches the activity summary.
	 * @param {string} userId - The user ID.
	 */
	GET_ACTIVITY_SUMMARY: `/:userId/Dashboard/activity-summary`,

	/**
	 * Fetches the metrics for student dashboard.
	 * @param {string} userId - The user ID.
	 * */
	GET_STUDENT_DASHBOARD_METRICS: `/:userId/StudentDashboard/metrics`,

	/**
	 * Fetches the completion of student profile
	 * @param {string} userId - The user ID.
	 */
	GET_STUDENT_PROFILE_COMPLETION: `/:userId/StudentDashboard/profile-completion`,

	/**
	 * Fetches recommended courses for a student
	 * @param {string} userId - The user ID.
	 */
	GET_RECOMMENDED_COURSES: `/:userId/StudentDashboard/recommended-courses`,

	/**
	 * Fetches monthly application stats.
	 * @param {string} userId - The user ID.
	 */
	GET_MONTHLY_STATS: `/:userId/Dashboard/monthly-stats`,

	/**
	 * Fetches companies interviewed.
	 * @param {string} userId - The user ID.
	 */
	GET_COMPANIES_INTERVIEWED: `/:userId/Dashboard/companies-interviewed`,

	/**
	 * Fetches weekly application stats.
	 * @param {string} userId - The user ID.
	 */
	GET_WEEKLY_STATS: `/:userId/Dashboard/weekly-stats`,

	/**
	 * Fetches all detailed analytics in one call.
	 */
	GET_DETAILED_ANALYTICS: `/:userId/Dashboard/detailed-analytics`,
};

export const JOBTRACKER_ENDPOINTS = {
	/**
	 * The base URL for the job tracker API.
	 */
	CONTROLLER: "api/Jobtracker",

	/**
	 * Fetches all job trackers by user ID.
	 * @param {string} userId - The user ID.
	 */
	GET_ALL_JOB_TRACKER: `/:userId`,

	/**
	 * Fetches a job tracker by job ID.
	 * @param {string} jobId - The job ID.
	 */
	GET_JOB_TRACKER_BY_ID: `/job/:jobId`,
	GET_JOB_FIT_CHECK: `/job/:jobId/fit-check`,
	SAVE_JOB_FIT_CHECK: `/job/:jobId/fit-check`,
	RUN_JOB_FIT_CHECK: `/job/:jobId/fit-check`,

	/**
	 * Fetches the status counts for a user.
	 * @param {string} userId - The user ID.
	 */
	GET_JOB_TRACKER_STATUS_COUNTS: `/status/:userId`,

	/**
	 * Adds a new job tracker entry.
	 */
	ADD_JOB_TRACKER: `/`,

	/**
	 * Updates an existing job tracker entry.
	 * @param {string} jobId - The job ID.
	 */
	UPDATE_JOB_TRACKER: `/:jobId`,

	/**
	 * Updates the status of a job tracker entry.
	 * @param {string} jobId - The job ID.
	 */
	UPDATE_JOB_TRACKER_STATUS: `/status/:jobId`,

	/**
	 * Updates the personal interest of a job tracker entry.
	 * @param {string} jobId - The job ID.
	 */
	UPDATE_JOB_TRACKER_PERSONAL_INTEREST: `/personal-interest/:jobId`,
	ATTACH_JOB_TRACKER_RESUME: `/:jobId/resume`,
	CLEAR_JOB_TRACKER_RESUME: `/:jobId/resume`,
	UPSERT_JOB_TRACKER_COVER_LETTER: `/:jobId/cover-letter`,
	CLEAR_JOB_TRACKER_COVER_LETTER: `/:jobId/cover-letter`,

	/**
	 * Deletes a job tracker entry.
	 * @param {string} jobId - The job ID.
	 */
	DELETE_JOB_TRACKER: `/:jobId`,

	/**
	 * Fetches dropdowns for the job tracker.
	 * @param {string} userId - The user ID.
	 */
	GET_JOB_TRACKER_DROPDOWNS: `/dropdown/:userId`,

	/**
	 * Fetches the job tracker as excel by user ID.
	 * @param {string} userId - The user ID.
	 */
	EXPORT_JOB_TRACKER: `/export/:userId`,

	/**
	 * Checks if url is already in job tracker.
	 * @param {string} url - The job URL. [Query parameter]
	 */
	CHECK_URL_IN_JOB_TRACKER: `/GetJobByUrl`,

	/**
	 * Gets company application history for a user.
	 * @param {string} userId - The user ID.
	 */
	GET_COMPANY_HISTORY: `/companyHistory/:userId`,

	/**
	 * Gets specific company application history by name.
	 * @param {string} userId - The user ID.
	 * @param {string} companyName - The company name. [Query parameter]
	 */
	GET_COMPANY_HISTORY_BY_NAME: `/companyHistory/:userId/company`,

	/**
	 * Checks if user applied to same position before.
	 * @param {string} userId - The user ID.
	 * @param {string} companyName - The company name. [Query parameter]
	 * @param {string} jobTitle - The job title. [Query parameter]
	 */
	CHECK_SAME_POSITION: `/checkSamePosition/:userId`,

	/**
	 * Gets company position history.
	 * @param {string} userId - The user ID.
	 * @param {string} companyName - The company name. [Query parameter]
	 */
	GET_COMPANY_POSITION_HISTORY: `/positionHistory/:userId/company`,

	/**
	 * Gets application insights for dashboard.
	 * @param {string} userId - The user ID.
	 */
	GET_APPLICATION_INSIGHTS: `/insights/:userId`,

	/**
	 * Gets companies currently recruiting.
	 */
	GET_RECRUITING_COMPANIES: `/recruiting-companies`,
};

export const SAVED_JOB_SEARCH_ENDPOINTS = {
	CONTROLLER: "api/SavedJobSearchs",
	GET_SAVED_SEARCHES: `/`,
	SAVE_SEARCH: `/`,
	REORDER_SEARCHES: `/reorder`,
	DELETE_SEARCH: `/:id`,
};

export const JOB_BROWSER_ENDPOINTS = {
	/**
	 * The base URL for the job browser API.
	 */
	CONTROLLER: "api/JobBrowser",

	/**
	 * Fetches all job browsers.
	 */
	GET_ALL_JOB_BROWSER: `/`,

	/**
	 * Fetches a job browser by ID.
	 * @param {string} id - The job browser ID.
	 */
	GET_JOB_BROWSER_BY_ID: `job/:id`,

	/**
	 * Fetches a public job browser by ID (no auth required).
	 * @param {string} id - The job browser ID.
	 */
	GET_PUBLIC_JOB_BROWSER_BY_ID: `public/job/:id`,

	/**
	 * Fetches lightweight sitemap items for recent public jobs.
	 * @param {number} months - Number of months to include, defaults to 3.
	 */
	GET_SITEMAP_JOB_BROWSER: `/sitemap`,

	/**
	 * Adds to the job tracker. (POST request)
	 * @param {string} id - The job browser ID.
	 * @param query - The user ID as a query parameter.
	 */
	ADD_TO_JOBTRACKER: `add/:id`,

	/**
	 * Updates an existing job browser entry. (PUT request)
	 * @param {string} id - The job browser ID.
	 */
	UPDATE_JOB_BROWSER: `/:id`,

	/**
	 * Deletes a job browser entry. (DELETE request)
	 * @param {string} id - The job browser ID.
	 */
	DELETE_JOB_BROWSER: `/:id`,

	/**
	 * Approves a job browser entry. (POST request)
	 * @param {string} id - The job browser ID.
	 */
	APPROVE_JOB_BROWSER: `approve/:id`,

	/**
	 * Batch approve and delete endpoints
	 */
	APPROVE_JOB_BROWSER_BATCH: `approve-batch`,

	DELETE_JOB_BROWSER_BATCH: `delete-batch`,

	/**
	 * Fetches all job browser approvals.
	 */
	GET_APPROVALS: `/approvals`,

	/**
	 * Fetches all countries by count.
	 */
	GET_COUNTRIES_BY_COUNT: `/countries`,
	GET_JOB_BROWSER_FACETS: `/facets`,
};

export const MAINTENANCE_ENDPOINTS = {
	/**
	 * The base URL for the maintenance API.
	 */
	CONTROLLER: "api/Maintenance",

	/**
	 * Fetches the maintenance status.
	 */
	GET_MAINTENANCE_STATUS: `/`,

	/**
	 * Creates a new maintenance status.
	 */
	CREATE_MAINTENANCE_STATUS: `/`,

	/**
	 * Updates an existing maintenance status.
	 */
	UPDATE_MAINTENANCE_STATUS: `/`,
};

export const PERMISSION_MANAGEMENT_ENDPOINTS = {
	/**
	 * The base URL for the permission management API.
	 */
	CONTROLLER: "api/PermissionManagement",

	/**
	 * Sets user claims.
	 */
	SET_USER_CLAIMS: `/set-user-claims`,

	/**
	 * Sets user role.
	 */
	SET_USER_ROLE: `/set-user-role`,

	/**
	 * Sets user access level.
	 */
	SET_USER_ACCESS_LEVEL: `/set-user-access-level`,

	/**
	 * Adds user permission.
	 */
	ADD_USER_PERMISSION: `/add-user-permission`,

	/**
	 * Removes user permission.
	 */
	REMOVE_USER_PERMISSION: `/remove-user-permission`,

	/**
	 * Checks permission.
	 */
	CHECK_PERMISSION: `/check-permission`,

	/**
	 * Checks page access.
	 */
	CHECK_PAGE_ACCESS: `/check-page-access`,

	/**
	 * Fetches user claims by user ID.
	 * @param {string} uid - The user ID.
	 */
	GET_USER_CLAIMS: `/user-claims/:uid`,

	/**
	 * Fetches current user claims.
	 */
	GET_CURRENT_USER_CLAIMS: `/current-user-claims`,

	/**
	 * Bulk updates user claims.
	 */
	BULK_USER_CLAIMS: `/bulk-user-claims`,

	/**
	 * Fetches admin page permissions.
	 */
	GET_ADMIN_PAGE_PERMISSIONS: `/admin-page-permissions`,

	/**
	 * Fetches current user info.
	 */
	GET_CURRENT_USER_INFO: `/current-user-info`,
};

export const PROFESSIONAL_PROFILE_ENDPOINTS = {
	/**
	 * The base URL for the professional profile API.
	 */
	CONTROLLER: "api/ProfessionalProfile",

	/**
	 * Fetches Professional Profile by user ID.
	 * @param {string} userId - The user ID.
	 * */
	GET_PROFESSIONAL_PROFILE: `user/:userId`,

	/**
	 * Updates Professional Profile by user ID.
	 * @param {string} userId - The user ID.
	 * */
	UPDATE_PROFESSIONAL_PROFILE: `user/:userId`,

	/**
	 * Deletes Professional Profile by user ID.
	 * @param {string} userId - The user ID.
	 * */
	DELETE_PROFESSIONAL_PROFILE: `user/:userId`,
};

export const REFERRAL_ENDPOINTS = {
	/**
	 * The base URL for the referral API.
	 */
	CONTROLLER: "api/JobReferral",

	/**
	 * Fetches all referrals.
	 */
	GET_ALL_REFERRALS: `/`,

	/**
	 * Fetches a referral by ID.
	 * @param {string} id - The referral ID.
	 */
	GET_REFERRAL_BY_ID: `/:id`,

	/**
	 * Fetches all referrals by user ID.
	 * @param {string} userId - The user ID.
	 * @param {string} [referralId] - Optional referral ID to filter by.
	 */
	GET_REFERRALS_BY_USER_ID: `/users/:userId/referrals`,

	/**
	 * Fetches all referrals by user ID with optional referral ID filter.
	 * @param {string} userId - The user ID.
	 * @param {QueryParameters} [params] - Optional query parameters.
	 */
	GET_APPLICATIONS_BY_USER_ID: `/users/:userId/applications`,

	/**
	 * Creates a new referral.
	 * @param {string} userId - The user ID.
	 */
	CREATE_NEW_REFERRAL: `/`,

	/**
	 * Updates an existing referral by ID.
	 * @param {string} id - The referral ID.
	 */
	UPADTE_REFERRAL: `/:id`,

	/**
	 * Deletes a referral by ID.
	 * @param {string} id - The referral ID.
	 */
	DELETE_REFERRAL: `/:id`,

	/**
	 * Fetches all referral applications by referral ID.
	 * @param {string} referralId - The referral ID.
	 */
	GET_REFERRAL_APPLICATIONS: `/:referralId/applications`,

	/**
	 * Applies for a referral.
	 * @param {string} referralId - The referral ID.
	 */
	APPLY_FOR_REFERRAL: `/:referralId/apply`,

	/**
	 * Updates the status of a referral application.
	 * @param {string} referralId - The referral ID.
	 * @param {string} applicationId - The application ID.
	 */
	UPDATE_REFERRAL_APPLICATION_STATUS: `/:referralId/applications/:applicationId`,

	/**
	 * Fetches chat for application by application ID.
	 * @param {string} applicationId - The application ID.
	 * @param {string} referralId - The referral ID.
	 * @param {string} userId - The user ID.
	 */
	GET_CHAT_FOR_APPLICATION: `/:referralId/applications/:applicationId/chat/:userId`,

	/**
	 * Sends a message in the chat for application by application ID.
	 * @param {string} applicationId - The application ID.
	 * @param {string} referralId - The referral ID.
	 * @param {string} userId - The user ID.
	 */
	SEND_MESSAGE_IN_CHAT: `/:referralId/applications/:applicationId/chat/:userId`,

	/**
	 * Fetches resume of an applicant.
	 * @param {string} referralId - The referral ID.
	 * @param {string} applicationId - The application ID.
	 */
	GET_APPLICANT_RESUME: `/:referralId/applications/:applicationId/resume`,

	/**
	 * Fetches status enums for referrals.
	 */
	GET_REFERRAL_STATUS_ENUMS: `/enums`,
};

export const REFERRAL_VERIFICATION_ENDPOINTS = {
	CONTROLLER: "api/referral-verifications",
	MINE: "/mine",
	START: "/",
	CONFIRM: "/confirm",
	MANUAL_REVIEW: "/manual-review",
	ADMIN: "/admin",
	DECISION: "/:id/decision",
};

export const RESUME_ENDPOINTS = {
	/**
	 * The base URL for the resume API.
	 */
	CONTROLLER: "api/Resume",

	/**
	 * Fetches all resumes by user ID.
	 * @param {string} userId - The user ID.
	 * @param {string} [params] - Optional query parameters.
	 */
	GET_ALL_RESUMES: `/user/:userId`,
	GET_RESUME_CARDS: `/user/:userId/cards`,

	/**
	 * Fetches a resume by ID.
	 * @param {string} resumeId - The resume ID.
	 */
	GET_RESUME_BY_ID: `/:resumeId`,

	/**
	 * Fetches basic info of all resumes by user ID.
	 * @param {string} userId - The user ID.
	 */
	GET_RESUME_BASIC_INFO: `/basic-info/:userId`,

	/**
	 * Creates a new resume for a user.
	 * @param {string} userId - The user ID.
	 */
	CREATE_NEW_RESUME: `/user/:userId`,

	/**
	 * Updates an existing resume by ID.
	 * @param {string} resumeId - The resume ID.
	 */
	UPADTE_RESUME: `/:resumeId`,

	/**
	 * Deletes a resume by ID.
	 * @param {string} resumeId - The resume ID.
	 */
	DELETE_RESUME: `/:resumeId`,
};

export const RESUME_COMPLETION_ENDPOINTS = {
	/**
	 * The base URL for the resume completion API.
	 */
	CONTROLLER: "api/ResumeCompletion",

	/**
	 * Fetches resume completion. (POST request)
	 * @param body - The resume to fetch completion for.
	 */
	CALCULATE_RESUME_COMPLETION: `/calculate`,

	/**
	 * Fetches a detailed resume completion. (POST request)
	 * @param body - The resume to fetch detailed completion for.
	 */
	CALCULATE_DETAILED_RESUME_COMPLETION: `/details`,
};

export const RESUME_TEMPLATE_ENDPOINTS = {
	/**
	 * The base URL for the resume template API.
	 */
	CONTROLLER: "api/ResumeTemplate",

	/**
	 * Fetches all resume templates.
	 */
	GET_ALL_RESUME_TEMPLATES: `/`,

	/**
	 * Fetches a resume template by ID.
	 * @param {string} id - The resume template ID.
	 */
	GET_RESUME_TEMPLATE_BY_ID: `/:id`,

	/**
	 * Creates a new resume template.
	 * @param body - The resume template data.
	 */
	CREATE_RESUME_TEMPLATE: `/`,

	/**
	 * Updates an existing resume template by ID.
	 * @param {string} id - The resume template ID.
	 * @param body - The updated resume template data.
	 */
	UPDATE_RESUME_TEMPLATE: `/:id`,

	/**
	 * Deletes a resume template by ID.
	 * @param {string} id - The resume template ID.
	 */
	DELETE_RESUME_TEMPLATE: `/:id`,
};

export const SHORTLIST_TRACKER_ENDPOINTS = {
	/**
	 * The base URL for the shortlist tracking API.
	 */
	CONTROLLER: "api/CourseShortlistingTracker",

	// ============================
	// TRACKER MANAGEMENT
	// ============================

	/**
	 * Creates a new course shortlisting tracker
	 * @method POST
	 * @body CreateTrackerRequest
	 * @returns CourseShortlistingTracker
	 */
	CREATE_TRACKER: `/`,

	/**
	 * Fetches all course shortlisting trackers by user ID
	 * @method GET
	 * @param {string} userId - The user ID
	 * @returns CourseShortlistingTracker[]
	 */
	GET_TRACKERS_BY_USER: `/user/:userId`,

	/**
	 * Fetches a specific tracker by tracker ID
	 * @method GET
	 * @param {string} trackerId - The tracker ID
	 * @returns CourseShortlistingTracker
	 */
	GET_TRACKER_BY_ID: `/:trackerId`,

	// ============================
	// STEP MANAGEMENT
	// ============================

	/**
	 * Fetches all steps for a specific tracker
	 * @method GET
	 * @param {string} trackerId - The tracker ID
	 * @returns CourseTrackerStepStatus[]
	 */
	GET_TRACKER_STEPS: `/:trackerId/steps`,

	/**
	 * Updates the status of a specific step
	 * @method PUT
	 * @param {string} trackerId - The tracker ID
	 * @param {string} stepId - The step ID
	 * @body UpdateStepStatusRequest
	 * @returns Success message
	 */
	UPDATE_STEP_STATUS: `/:trackerId/steps/:stepId/status`,

	// ============================
	// SUBSTEP MANAGEMENT
	// ============================

	/**
	 * Fetches all substeps for a specific tracker or step
	 * @method GET
	 * @param {string} trackerId - The tracker ID
	 * @query {string} stepId - Optional step ID to filter substeps
	 * @returns CourseTrackerSubStepStatus[]
	 */
	GET_TRACKER_SUBSTEPS: `/:trackerId/substeps`,

	/**
	 * Updates the status of a specific substep
	 * @method PUT
	 * @param {string} trackerId - The tracker ID
	 * @param {string} subStepId - The substep ID
	 * @body UpdateStepStatusRequest
	 * @returns Success message
	 */
	UPDATE_SUBSTEP_STATUS: `/:trackerId/substeps/:subStepId/status`,

	// ============================
	// TEMPLATE MANAGEMENT
	// ============================

	/**
	 * Initializes default templates for common countries and course types
	 * @method POST
	 * @returns Success message
	 */
	INITIALIZE_TEMPLATES: `/initialize-templates`,

	/**
	 * Creates or gets a template version for specific country and course type
	 * @method POST
	 * @body CreateTemplateRequest
	 * @returns TrackerTemplateVersion
	 */
	CREATE_TEMPLATE: `/template`,

	/**
	 * Checks if an active template exists for a country and course type
	 * @method GET
	 * @query {string} countryId - The country ID
	 * @query {string} courseTypeId - The course type ID
	 * @query {string} versionName - Optional version name (defaults to v1.0)
	 * @returns boolean
	 */
	HAS_ACTIVE_TEMPLATE: `/template/exists`,

	/**
	 * Gets the active template for a country and course type
	 * @method GET
	 * @query {string} countryId - The country ID
	 * @query {string} courseTypeId - The course type ID
	 * @query {string} versionName - Optional version name (defaults to v1.0)
	 * @returns TrackerTemplateVersion
	 */
	GET_ACTIVE_TEMPLATE: `/template/active`,

	// ============================
	// ANALYTICS & STATISTICS
	// ============================

	/**
	 * Fetches university processing statistics
	 * @method GET
	 * @param {string} universityId - The university ID
	 * @returns UniversityProcessingStats
	 */
	GET_UNIVERSITY_STATS: `/university/:universityId/stats`,

	/**
	 * Fetches trackers by country and course type for analytics
	 * @method GET
	 * @query {string} countryId - The country ID
	 * @query {string} courseTypeId - The course type ID
	 * @returns CourseShortlistingTracker[]
	 */
	GET_TRACKERS_ANALYTICS: `/analytics`,

	// ============================
	// DASHBOARD
	// ============================

	/**
	 * Fetches dashboard data for user's course tracking
	 * @method GET
	 * @param {string} userId - The user ID
	 * @returns TrackerDashboardData
	 */
	GET_DASHBOARD: `/dashboard/:userId`,
};

export const STUDENT_PROFILE_ENDPOINTS = {
	/**
	 * The base URL for the student profile API.
	 */
	CONTROLLER: "api/StudentProfile",

	/**
	 * Fetches Student Profile by user ID.
	 * @param {string} userId - The user ID.
	 * */
	GET_STUDENT_PROFILE: `user/:userId`,

	/**
	 * Updates Student Profile by user ID.
	 * @param {string} userId - The user ID.
	 * */
	UPDATE_STUDENT_PROFILE: `user/:userId`,

	/**
	 * Deletes Student Profile by user ID.
	 * @param {string} userId - The user ID.
	 * */
	DELETE_STUDENT_PROFILE: `user/:userId`,
};

export const USER_AUTHORIZATION_ENDPOINTS = {
	/**
	 * The base URL for the user authorization API.
	 */
	CONTROLLER: "api/UserAuthorization",

	/**
	 * Gets the current user
	 */
	GET_CURRENT_USER: `/`,

	/**
	 * Decodes the session cookie
	 */
	DECODE_SESSION_COOKIE: `/decode-session`,

	/**
	 * Sets session cookie
	 * @param {string} token - The authentication token.
	 */
	SET_SESSION_COOKIE: `/session`,

	/**
	 * Sets user role
	 * @param {string} uid - The user ID.
	 * @param {string} role - The role to set. [in body]
	 */
	SET_USER_ROLE: `/users/:uid/role`,

	/**
	 * Validates user
	 * @param {string} uid - The user ID.
	 */
	VALIDATE_USER: `/users/:uid/validate`,

	GET_USER_CLAIMS: `/users/:uid/claims`,

	UPDATE_TUTORIAL_STATUS: `/tutorial-status`,
};

export const USER_INFO_ENDPOINTS = {
	/**
	 * The base URL for the user info API.
	 */
	CONTROLLER: "api/UserInfo",

	/**
	 * Fetches all user info.
	 */
	GET_ALL_USER_INFO: `/`,

	/**
	 * Fetches user info by user ID.
	 * @param {string} userId - The user ID.
	 */
	GET_USER_INFO: `/:userId`,

	/**
	 * Updates user info by user ID.
	 * @param {string} userId - The user ID.
	 */
	UPDATE_USER_INFO: `/:userId`,
};

export const USER_ENDPOINTS = {
	/**
	 * The base URL for the user API.
	 */
	CONTROLLER: "api/UserServices",

	/**
	 * Syncs user info by user ID. (GET request)
	 * @param {string} userId - The user ID. [From params]
	 */
	SYNC_USER_INFO: `/SyncSingleUser`,

	/**
	 * Sends the welcome email for a verified user. (POST request)
	 * @param {string} uid - The Firebase UID. [From Query]
	 */
	SEND_WELCOME_EMAIL: `/SendWelcomeEmail`,

	/**
	 * Fetches all users.
	 */
	GET_ALL_USERS: `/`,

	/**
	 * Deletes a user by firebaseUid and reasonForDeletion. (DELETE request)
	 * @param {string} firebaseUid - The Firebase UID of the user. [From Query]
	 * @param {string} reasonForDeletion - The reason for deletion. [From Query]
	 */
	DELETE_USER: "/DeleteUser",

	/**
	 * Toggles self paused state. (POST request)
	 */
	TOGGLE_PAUSE: "/toggle-pause",
};

export const USER_MANAGEMENT_ENDPOINTS = {
	/**
	 * The base URL for the user management API.
	 */
	CONTROLLER: "api/UserManagement",

	/**
	 * Fetches all users.
	 */
	GET_ALL_USERS: `/`,

	/**
	 * Fetch user info by user ID.
	 * @param {string} userId - The user ID.
	 * */
	GET_USER_INFO: `/:userId`,
	GET_ACTIVE_SUMMARY: `/active-summary`,


	/**
	 * Deletes a user by user ID.
	 * @param {string} userId - The user ID.
	 * */
	DELETE_USER: `/:userId`,

	/**
	 * Updates role of a user by user ID.
	 * @param {string} userId - The user ID.
	 */
	UPDATE_USER_ROLE: `/:userId/role`,

	/**
	 * Toggles user paused state by user ID.
	 * @param {string} userId - The user ID.
	 */
	TOGGLE_PAUSE: `/:userId/toggle-pause`,

	/**
	 * Exports users data to CSV.
	 */
	EXPORT_USER_DATA: `/export`,

	/**
	 * Fetches dropdowns for admin user manager
	 */
	GET_DROPDOWNS: `/dropdowns`,

	/**
	 * Fetches paginated deleted users with optional search.
	 * @method GET
	 * @query {QueryParameters} queryParams
	 * @returns PagedResponse<DeletedUserDto>
	 */
	GET_DELETED_USERS: `/deleted`,
};

export const WARNING_ENDPOINTS = {
	/**
	 * The base URL for the warning API.
	 * */
	CONTROLLER: "api/Warning",

	/**
	 * Fetches all warnings.
	 * */
	GET_ALL_WARNINGS: `/`,

	/**
	 * Creates a new warning.
	 * */
	CREATE_WARNING: `/`,

	/**
	 * Updates an existing warning by ID.
	 * @param {string} id - The warning ID.
	 * */
	UPDATE_WARNING: `/:id`,

	/**
	 * Deletes a warning by ID.
	 * @param {string} id - The warning ID.
	 * */
	DELETE_WARNING: `/:id`,
};

export const TRANSLATE_ENDPOINTS = {
	/**
	 * The base URL for the translation API.
	 * Requires X-API-Key header for authentication.
	 */
	CONTROLLER: "api",

	/**
	 * Translates text to target language.
	 * Method: POST
	 * Headers: X-API-Key
	 * @param {Object} body - Request body
	 * @param {string} body.text - Text to translate
	 * @param {string} body.target_language - Target language code
	 * @returns {Object} Response object
	 * @returns {string} response.translated_text - Translated text
	 * @returns {string} response.source_language - Detected source language
	 * @returns {string} response.target_language - Target language
	 * @returns {number} response.confidence - Detection confidence
	 */
	TRANSLATE_TEXT: `/translate`,

	/**
	 * Fetches all supported languages.
	 * Method: GET
	 * Headers: X-API-Key
	 * @returns {Object} Response object
	 * @returns {Object} response.languages - Map of language codes to names
	 * @returns {number} response.count - Total number of supported languages
	 */
	GET_SUPPORTED_LANGUAGES: `/languages`,

	DETECT_LANGUAGE: `/detect-language`,
};

export const UTILS_ENDPOINTS = {
	/**
	 * The base URL for the utils API.
	 */
	CONTROLLER: "api/Utils",

	/**
	 * Fetches all job types.
	 * @returns {Array<JobTypeResponse>} Array of job types with id, type and description.
	 */
	GET_JOB_TYPES: `/jobtypes`,
};

export const ADMIN_COURSE_ENDPOINTS = {
	/**
	 * The base URL for the course template admin API.
	 */
	CONTROLLER: "api/admin",

	/**
	 * Fetches simplified universities.
	 * @param {Object} queryParams - Optional query parameters
	 */
	GET_SIMPLIFIED_UNIVERSITIES: `/University/simplified`,

	/**
	 * Fetches all universities.
	 * @param {Object} queryParams - Optional query parameters
	 */
	GET_ALL_UNIVERSITIES: `/University`,

	/**
	 * Adds a new university.
	 */
	ADD_UNIVERSITY: `/University`,

	/**
	 * Updates an existing university.
	 * @param {string} id - University ID
	 */
	UPDATE_UNIVERSITY: `/University/:id`,

	/**
	 * Deletes a university.
	 * @param {string} id - University ID
	 */
	DELETE_UNIVERSITY: `/University/:id`,

	/**
	 * Fetches all university types.
	 */
	GET_ALL_UNIVERSITY_TYPES: `/UniversityType`,

	/**
	 * Adds a new university type.
	 */
	ADD_UNIVERSITY_TYPE: `/UniversityType`,

	/**
	 * Updates an existing university type.
	 * @param {string} id - University type ID
	 */
	UPDATE_UNIVERSITY_TYPE: `/UniversityType/:id`,

	/**
	 * Deletes a university type.
	 * @param {string} id - University type ID
	 */
	DELETE_UNIVERSITY_TYPE: `/UniversityType/:id`,

	/**
	 * Fetches all courses.
	 * @param {Object} queryParams - Optional query parameters
	 */
	GET_ALL_COURSES: `/Course`,

	/**
	 * Adds a new course.
	 */
	ADD_COURSE: `/Course`,

	/**
	 * Updates an existing course.
	 * @param {string} id - Course ID
	 */
	UPDATE_COURSE: `/Course/:id`,

	/**
	 * Deletes a course.
	 * @param {string} id - Course ID
	 */
	DELETE_COURSE: `/Course/:id`,

	/**
	 * Fetches all countries.
	 */
	GET_ALL_COUNTRIES: `/Country`,

	/**
	 * Adds a new country.
	 */
	ADD_COUNTRY: `/Country`,

	/**
	 * Updates an existing country.
	 * @param {string} id - Country ID
	 */
	UPDATE_COUNTRY: `/Country/:id`,

	/**
	 * Deletes a country.
	 * @param {string} id - Country ID
	 */
	DELETE_COUNTRY: `/Country/:id`,

	/**
	 * Fetches a country by ID.
	 * @param {string} id - Country ID
	 */
	GET_COUNTRY_BY_ID: `/Country/:id`,

	/**
	 * Fetches all course types.
	 */
	GET_ALL_COURSE_TYPES: `/CourseType`,

	/**
	 * Adds a new course type.
	 */
	ADD_COURSE_TYPE: `/CourseType`,

	/**
	 * Updates an existing course type.
	 * @param {string} id - Course type ID
	 */
	UPDATE_COURSE_TYPE: `/CourseType/:id`,

	/**
	 * Deletes a course type.
	 * @param {string} id - Course type ID
	 */
	DELETE_COURSE_TYPE: `/CourseType/:id`,

	/**
	 * Fetches all application types.
	 */
	GET_ALL_APPLICATION_TYPES: `/ApplicationType`,

	/**
	 * Adds a new application type.
	 */
	ADD_APPLICATION_TYPE: `/ApplicationType`,

	/**
	 * Updates an existing application type.
	 * @param {string} id - Application type ID
	 */
	UPDATE_APPLICATION_TYPE: `/ApplicationType/:id`,

	/**
	 * Deletes an application type.
	 * @param {string} id - Application type ID
	 */
	DELETE_APPLICATION_TYPE: `/ApplicationType/:id`,

	/**
	 * Fetches all course sub types.
	 */
	GET_ALL_COURSE_SUB_TYPES: `/CourseSubType`,

	/**
	 * Adds a new course sub type.
	 */
	ADD_COURSE_SUB_TYPE: `/CourseSubType`,

	/**
	 * Updates an existing course sub type.
	 * @param {string} id - Course sub type ID
	 */
	UPDATE_COURSE_SUB_TYPE: `/CourseSubType/:id`,

	/**
	 * Deletes a course sub type.
	 * @param {string} id - Course sub type ID
	 */
	DELETE_COURSE_SUB_TYPE: `/CourseSubType/:id`,

	/**
	 * Fetches a course sub type by ID.
	 * @param {string} id - Course sub type ID
	 */
	GET_COURSE_SUB_TYPE_BY_ID: `/CourseSubType/:id`,

	/**
	 * Fetches all course languages.
	 */
	GET_ALL_COURSE_LANGUAGES: `/CourseLanguages`,

	/**
	 * Adds a new course language.
	 */
	ADD_COURSE_LANGUAGE: `/CourseLanguages`,

	/**
	 * Updates an existing course language.
	 * @param {string} id - Course language ID
	 */
	UPDATE_COURSE_LANGUAGE: `/CourseLanguages/:id`,

	/**
	 * Deletes a course language.
	 * @param {string} id - Course language ID
	 */
	DELETE_COURSE_LANGUAGE: `/CourseLanguages/:id`,

	/**
	 * Fetches a course language by ID.
	 * @param {string} id - Course language ID
	 */
	GET_COURSE_LANGUAGE_BY_ID: `/CourseLanguages/:id`,

	/**
	 * Fetches all tuition fee data.
	 */
	GET_ALL_TUITION_FEE_DATA: `/TuitionFeeData`,

	/**
	 * Adds new tuition fee data.
	 */
	ADD_TUITION_FEE_DATA: `/TuitionFeeData`,

	/**
	 * Updates existing tuition fee data.
	 * @param {string} id - Tuition fee data ID
	 */
	UPDATE_TUITION_FEE_DATA: `/TuitionFeeData/:id`,

	/**
	 * Deletes tuition fee data.
	 * @param {string} id - Tuition fee data ID
	 */
	DELETE_TUITION_FEE_DATA: `/TuitionFeeData/:id`,

	/**
	 * Fetches tuition fee data by ID.
	 * @param {string} id - Tuition fee data ID
	 */
	GET_TUITION_FEE_DATA_BY_ID: `/TuitionFeeData/:id`,

	/**
	 * Fetches all duration data.
	 */
	GET_ALL_DURATION: `/Duration`,

	/**
	 * Adds new duration data.
	 */
	ADD_DURATION: `/Duration`,

	/**
	 * Updates existing duration data.
	 * @param {string} id - Duration ID
	 */
	UPDATE_DURATION: `/Duration/:id`,

	/**
	 * Deletes duration data.
	 * @param {string} id - Duration ID
	 */
	DELETE_DURATION: `/Duration/:id`,

	/**
	 * Fetches duration data by ID.
	 * @param {string} id - Duration ID
	 */
	GET_DURATION_BY_ID: `/Duration/:id`,

	/**
	 * Fetches all application timeline info.
	 */
	GET_ALL_APPLICATION_TIMELINE: `/ApplicationTimelineInfo`,

	/**
	 * Adds new application timeline info.
	 */
	ADD_APPLICATION_TIMELINE: `/ApplicationTimelineInfo`,

	/**
	 * Updates existing application timeline info.
	 * @param {string} id - Application timeline ID
	 */
	UPDATE_APPLICATION_TIMELINE: `/ApplicationTimelineInfo/:id`,

	/**
	 * Deletes application timeline info.
	 * @param {string} id - Application timeline ID
	 */
	DELETE_APPLICATION_TIMELINE: `/ApplicationTimelineInfo/:id`,

	/**
	 * Fetches application timeline info by ID.
	 * @param {string} id - Application timeline ID
	 */
	GET_APPLICATION_TIMELINE_BY_ID: `/ApplicationTimelineInfo/:id`,

	/**
	 * Fetches all extra information.
	 */
	GET_ALL_EXTRA_INFORMATION: `/ExtraInformation`,

	/**
	 * Adds new extra information.
	 */
	ADD_EXTRA_INFORMATION: `/ExtraInformation`,

	/**
	 * Updates existing extra information.
	 * @param {string} id - Extra information ID
	 */
	UPDATE_EXTRA_INFORMATION: `/ExtraInformation/:id`,

	/**
	 * Deletes extra information.
	 * @param {string} id - Extra information ID
	 */
	DELETE_EXTRA_INFORMATION: `/ExtraInformation/:id`,

	/**
	 * Fetches extra information by ID.
	 * @param {string} id - Extra information ID
	 */
	GET_EXTRA_INFORMATION_BY_ID: `/ExtraInformation/:id`,
};

export const HEALTH_CHECK_ENDPOINTS = {
	/**
	 * The base URL for the health check API.
	 */
	CONTROLLER: "api",

	/**
	 * Fetches the backend API status.
	 */
	GET_BACKEND_STATUS: `/Status`,

	/**
	 * Fetches the AI API status.
	 */
	GET_AI_STATUS: `/data`,
};

export const RESUME_AI_PROBLEM_ENDPOINTS = {
	/**
	 * The base URL for the resume AI problem API.
	 */
	CONTROLLER: "api/ResumeAIProblem",

	/**
	 * Gets AI problems for a specific resume.
	 * @param {string} resumeId - The resume ID.
	 */
	GET_BY_RESUME_ID: `/resume/:resumeId`,

	/**
	 * Creates or updates AI problems for a resume.
	 * @param {ResumeAIProblemDto} dto - The AI problems data.
	 */
	CREATE_OR_UPDATE: `/`,

	/**
	 * Deletes AI problems for a resume.
	 * @param {string} resumeId - The resume ID.
	 */
	DELETE_BY_RESUME_ID: `/resume/:resumeId`,

	/**
	 * Gets all AI problems for a user's resumes.
	 * @param {string} userId - The user ID.
	 */
	GET_BY_USER: `/user/:userId`,
};

export const RESUME_AI_ANALYSIS_ENDPOINTS = {
	/**
	 * The base URL for the resume AI analysis API.
	 */
	CONTROLLER: "api/ResumeAI",

	/**
	 * Submit a resume for AI analysis.
	 * @param {ResumeAIRequestDto} dto - The analysis request data.
	 */
	ANALYZE: `/analyze`,

	/**
	 * Get the status of a resume AI processing request.
	 * @param {string} processingId - The processing request ID.
	 * @param {string} resumeId - The resume ID.
	 */
	STATUS: `/status/resume/:resumeId`,

	/**
	 * Get processing history for a user.
	 * @param {number} pageNumber - Page number (default: 1).
	 * @param {number} pageSize - Page size (default: 10).
	 */
	HISTORY: `/history`,

	/**
	 * Cancel a queued processing request.
	 * @param {string} processingId - The processing request ID.
	 */
	CANCEL: `/cancel/:processingId`,

	/**
	 * Get current queue status.
	 */
	QUEUE_STATUS: `/queue/status`,

	/**
	 * Check if user is rate limited.
	 */
	RATE_LIMIT: `/ratelimit`,
};

export const QUICK_LINK_ENDPOINTS = {
	/**
	 * The base URL for the quick link API.
	 */
	CONTROLLER: "api/QuickLink",

	/**
	 * Get all quick links for the authenticated user.
	 * @method GET
	 * @returns QuickLinkDto[]
	 */
	GET_USER_QUICK_LINKS: `/`,

	/**
	 * Get a specific quick link by ID.
	 * @method GET
	 * @param {string} id - The quick link ID.
	 * @returns QuickLinkDto
	 */
	GET_QUICK_LINK_BY_ID: `/:id`,

	/**
	 * Create a new quick link (max 5 per user).
	 * @method POST
	 * @body CreateQuickLinkDto
	 * @returns QuickLinkDto
	 */
	CREATE_QUICK_LINK: `/`,

	/**
	 * Update an existing quick link.
	 * @method PUT
	 * @param {string} id - The quick link ID.
	 * @body UpdateQuickLinkDto
	 * @returns QuickLinkDto
	 */
	UPDATE_QUICK_LINK: `/:id`,

	/**
	 * Delete a quick link.
	 * @method DELETE
	 * @param {string} id - The quick link ID.
	 * @returns { message: string, deleted: boolean }
	 */
	DELETE_QUICK_LINK: `/:id`,
};

export const GRAPH_EMAIL_ENDPOINTS = {
	CONTROLLER: "api/GraphEmail",
	SEND_EMAIL: "/send",
};

export const GLOBAL_PROCESS_TRACKING_ENDPOINTS = {
	/**
	 * The base URL for the global process tracking API.
	 */
	CONTROLLER: "api/GlobalProcessTracking",

	// ============================
	// GLOBAL PROCESS TRACKER MANAGEMENT
	// ============================

	/**
	 * Create a new global process tracker for a user
	 * @method POST
	 * @body CreateGlobalProcessTrackerRequestDTO
	 * @returns GlobalProcessTrackerDTO
	 */
	CREATE_GLOBAL_PROCESS_TRACKER: `/tracker`,

	/**
	 * Get the global process tracker for the current user
	 * @method GET
	 * @returns GlobalProcessTrackerDTO
	 */
	GET_GLOBAL_PROCESS_TRACKER: `/tracker`,

	/**
	 * Get a global process tracker by ID
	 * @method GET
	 * @param {string} trackerId - The tracker ID
	 * @returns GlobalProcessTrackerDTO
	 */
	GET_GLOBAL_PROCESS_TRACKER_BY_ID: `/tracker/:trackerId`,

	/**
	 * Update a global process tracker
	 * @method PUT
	 * @param {string} trackerId - The tracker ID
	 * @body UpdateGlobalProcessTrackerRequestDTO
	 * @returns GlobalProcessTrackerDTO
	 */
	UPDATE_GLOBAL_PROCESS_TRACKER: `/tracker/:trackerId`,

	/**
	 * Delete a global process tracker
	 * @method DELETE
	 * @param {string} trackerId - The tracker ID
	 * @returns boolean
	 */
	DELETE_GLOBAL_PROCESS_TRACKER: `/tracker/:trackerId`,

	// ============================
	// PROCESS INSTANCE MANAGEMENT
	// ============================

	/**
	 * Create a new process instance
	 * @method POST
	 * @body CreateProcessInstanceRequestDTO
	 * @returns ProcessInstanceDTO
	 */
	CREATE_PROCESS_INSTANCE: `/process-instance`,

	/**
	 * Get a process instance by ID
	 * @method GET
	 * @param {string} processInstanceId - The process instance ID
	 * @returns ProcessInstanceDTO
	 */
	GET_PROCESS_INSTANCE: `/process-instance/:processInstanceId`,

	/**
	 * Get all process instances for the current user
	 * @method GET
	 * @returns ProcessInstanceDTO[]
	 */
	GET_USER_PROCESS_INSTANCES: `/process-instances`,

	/**
	 * Update process instance status
	 * @method PUT
	 * @param {string} processInstanceId - The process instance ID
	 * @body UpdateProcessStatusRequestDTO
	 * @returns ProcessInstanceDTO
	 */
	UPDATE_PROCESS_INSTANCE_STATUS: `/process-instance/:processInstanceId/status`,

	/**
	 * Update process instance priority
	 * @method PUT
	 * @param {string} processInstanceId - The process instance ID
	 * @body UpdateProcessPriorityRequestDTO
	 * @returns ProcessInstanceDTO
	 */
	UPDATE_PROCESS_INSTANCE_PRIORITY: `/process-instance/:processInstanceId/priority`,

	/**
	 * Delete a process instance
	 * @method DELETE
	 * @param {string} processInstanceId - The process instance ID
	 * @returns boolean
	 */
	DELETE_PROCESS_INSTANCE: `/process-instance/:processInstanceId`,

	// ============================
	// PROCESS TEMPLATE MANAGEMENT
	// ============================

	/**
	 * Get available process templates for a country
	 * @method GET
	 * @param {string} countryId - The country ID
	 * @returns ProcessTemplateDTO[]
	 */
	GET_AVAILABLE_PROCESS_TEMPLATES: `/templates/country/:countryId`,

	/**
	 * Get a specific process template
	 * @method GET
	 * @param {ProcessType} processType - The process type
	 * @param {string} countryId - The country ID
	 * @param {string} [version] - The version (default: v1.0)
	 * @returns ProcessTemplateDTO
	 */
	GET_PROCESS_TEMPLATE: `/templates/:processType/country/:countryId`,

	/**
	 * Initialize default process templates (Admin only)
	 * @method POST
	 * @returns boolean
	 */
	INITIALIZE_DEFAULT_PROCESS_TEMPLATES: `/templates/initialize`,

	/**
	 * Create a new process template (Admin only)
	 * @method POST
	 * @body CreateProcessTemplateRequestDTO
	 * @returns ProcessTemplateDTO
	 */
	CREATE_TEMPLATE: `/templates`,

	/**
	 * Update an existing process template (Admin only)
	 * @method PUT
	 * @param {string} templateId - The template ID
	 * @body UpdateProcessTemplateRequestDTO
	 * @returns ProcessTemplateDTO
	 */
	UPDATE_TEMPLATE: `/templates/:templateId`,

	/**
	 * Delete a process template (Admin only)
	 * @method DELETE
	 * @param {string} templateId - The template ID
	 * @returns boolean
	 */
	DELETE_TEMPLATE: `/templates/:templateId`,

	/**
	 * Get list of active process templates
	 * @method GET
	 * @returns number[]
	 */
	GET_ACTIVE_TEMPLATES: `/templates/:countryId/available-process-types`,

	// ============================
	// STEP STATUS MANAGEMENT
	// ============================

	/**
	 * Update process step status
	 * @method PUT
	 * @param {string} processInstanceId - The process instance ID
	 * @param {string} processStepId - The process step ID
	 * @body UpdateProcessStepStatusRequestDTO
	 * @returns ProcessStepStatusDTO
	 */
	UPDATE_PROCESS_STEP_STATUS: `/process-instance/:processInstanceId/step/:processStepId/status`,

	/**
	 * Get process step statuses for a process instance
	 * @method GET
	 * @param {string} processInstanceId - The process instance ID
	 * @returns ProcessStepStatusDTO[]
	 */
	GET_PROCESS_STEP_STATUSES: `/process-instance/:processInstanceId/step-statuses`,

	/**
	 * Get a specific process step status
	 * @method GET
	 * @param {string} processInstanceId - The process instance ID
	 * @param {string} processStepId - The process step ID
	 * @returns ProcessStepStatusDTO
	 */
	GET_PROCESS_STEP_STATUS: `/process-instance/:processInstanceId/step/:processStepId/status`,

	// ============================
	// DOCUMENT MANAGEMENT
	// ============================

	/**
	 * Add a process document
	 * @method POST
	 * @body AddProcessDocumentRequestDTO
	 * @returns ProcessDocumentDTO
	 */
	ADD_PROCESS_DOCUMENT: `/documents`,

	/**
	 * Update process document status
	 * @method PUT
	 * @param {string} documentId - The document ID
	 * @body UpdateDocumentStatusRequestDTO
	 * @returns ProcessDocumentDTO
	 */
	UPDATE_PROCESS_DOCUMENT_STATUS: `/documents/:documentId/status`,

	/**
	 * Get documents for a process instance
	 * @method GET
	 * @param {string} processInstanceId - The process instance ID
	 * @returns ProcessDocumentDTO[]
	 */
	GET_PROCESS_DOCUMENTS: `/process-instance/:processInstanceId/documents`,

	/**
	 * Get documents for a process step
	 * @method GET
	 * @param {string} processStepStatusId - The process step status ID
	 * @returns ProcessDocumentDTO[]
	 */
	GET_STEP_DOCUMENTS: `/step/:processStepStatusId/documents`,

	/**
	 * Delete a process document
	 * @method DELETE
	 * @param {string} documentId - The document ID
	 * @returns boolean
	 */
	DELETE_PROCESS_DOCUMENT: `/documents/:documentId`,

	// ============================
	// REMINDER MANAGEMENT
	// ============================

	/**
	 * Create a process reminder
	 * @method POST
	 * @body CreateProcessReminderRequestDTO
	 * @returns ProcessReminderDTO
	 */
	CREATE_PROCESS_REMINDER: `/reminders`,

	/**
	 * Get pending reminders (Admin only)
	 * @method GET
	 * @returns ProcessReminderDTO[]
	 */
	GET_PENDING_REMINDERS: `/reminders/pending`,

	/**
	 * Get user reminders
	 * @method GET
	 * @returns ProcessReminderDTO[]
	 */
	GET_USER_REMINDERS: `/reminders`,

	/**
	 * Mark reminder as sent (Admin only)
	 * @method PUT
	 * @param {string} reminderId - The reminder ID
	 * @returns boolean
	 */
	MARK_REMINDER_AS_SENT: `/reminders/:reminderId/sent`,

	/**
	 * Delete a reminder
	 * @method DELETE
	 * @param {string} reminderId - The reminder ID
	 * @returns boolean
	 */
	DELETE_REMINDER: `/reminders/:reminderId`,

	// ============================
	// DASHBOARD AND ANALYTICS
	// ============================

	/**
	 * Get user dashboard data
	 * @method GET
	 * @returns GlobalProcessDashboardDTO
	 */
	GET_USER_DASHBOARD: `/dashboard`,

	/**
	 * Get upcoming deadlines
	 * @method GET
	 * @param {number} [daysAhead] - Days ahead to look for deadlines (default: 30)
	 * @returns UpcomingDeadlineDTO[]
	 */
	GET_UPCOMING_DEADLINES: `/dashboard/deadlines`,

	/**
	 * Get critical actions
	 * @method GET
	 * @returns CriticalActionDTO[]
	 */
	GET_CRITICAL_ACTIONS: `/dashboard/critical-actions`,

	/**
	 * Get progress summary
	 * @method GET
	 * @returns ProcessProgressSummaryDTO
	 */
	GET_PROGRESS_SUMMARY: `/dashboard/progress-summary`,

	/**
	 * Get recent activities
	 * @method GET
	 * @param {number} [limit] - Number of activities to return (default: 20)
	 * @returns RecentActivityDTO[]
	 */
	GET_RECENT_ACTIVITIES: `/dashboard/recent-activities`,

	// ============================
	// ANALYTICS AND REPORTING (Admin Only)
	// ============================

	/**
	 * Get global analytics (Admin only)
	 * @method GET
	 * @returns GlobalProcessAnalyticsDTO
	 */
	GET_GLOBAL_ANALYTICS: `/analytics/global`,

	/**
	 * Get completion statistics (Admin only)
	 * @method GET
	 * @query {string} [countryId] - The country ID
	 * @query {ProcessType} [processType] - The process type
	 * @returns ProcessCompletionStatsDTO
	 */
	GET_COMPLETION_STATS: `/analytics/completion`,

	/**
	 * Get process type analytics (Admin only)
	 * @method GET
	 * @query {string} [countryId] - The country ID
	 * @returns ProcessTypeAnalyticsDTO
	 */
	GET_PROCESS_TYPE_ANALYTICS: `/analytics/process-types`,

	/**
	 * Get country analytics (Admin only)
	 * @method GET
	 * @returns CountryAnalyticsDTO
	 */
	GET_COUNTRY_ANALYTICS: `/analytics/countries`,

	/**
	 * Get bottleneck analysis (Admin only)
	 * @method GET
	 * @query {number} [daysStuckThreshold] - The threshold in days for a process to be considered stuck (default: 30)
	 * @returns BottleneckAnalysisDTO
	 */
	GET_BOTTLENECK_ANALYSIS: `/analytics/bottlenecks`,

	/**
	 * Get time-based analytics (Admin only)
	 * @method GET
	 * @query {number} [months] - The number of months to look back (default: 12)
	 * @returns TimeBasedAnalyticsDTO
	 */
	GET_TIME_BASED_ANALYTICS: `/analytics/time-based`,

	// ============================
	// PROGRESS CALCULATION
	// ============================

	/**
	 * Calculate global progress
	 * @method GET
	 * @param {string} globalProcessTrackerId - The global process tracker ID
	 * @returns ProgressResponse
	 */
	CALCULATE_GLOBAL_PROGRESS: `/tracker/:globalProcessTrackerId/progress`,

	/**
	 * Calculate process progress
	 * @method GET
	 * @param {string} processInstanceId - The process instance ID
	 * @returns ProgressResponse
	 */
	CALCULATE_PROCESS_PROGRESS: `/process-instance/:processInstanceId/progress`,

	// ============================
	// UTILITY METHODS
	// ============================

	/**
	 * Check if a process is blocked by dependencies
	 * @method GET
	 * @param {string} processInstanceId - The process instance ID
	 * @returns BlockedStatusResponse
	 */
	CHECK_PROCESS_BLOCKED: `/process-instance/:processInstanceId/blocked`,

	/**
	 * Get blocked processes for current user
	 * @method GET
	 * @returns ProcessInstanceDTO[]
	 */
	GET_BLOCKED_PROCESSES: `/blocked-processes`,

	/**
	 * Get overdue steps for current user
	 * @method GET
	 * @returns ProcessStepStatusDTO[]
	 */
	GET_OVERDUE_STEPS: `/overdue-steps`,
};

export const AI_USAGE_ENDPOINTS = {
	/**
	 * The base URL for the AI Usage API.
	 */
	CONTROLLER: "api/AIUsage",

	// ============================
	// USAGE VERIFICATION AND RECORDING ENDPOINTS
	// ============================

	/**
	 * Verifies if the current user can use a specific AI service
	 * @method POST
	 * @body VerifyAIUsageRequestDto
	 * @returns VerifyAIUsageResponseDto
	 */
	VERIFY_USAGE: `/verify`,

	/**
	 * Records AI service usage for the current user
	 * @method POST
	 * @body RecordAIUsageRequestDto
	 * @returns RecordAIUsageResponseDto
	 */
	RECORD_USAGE: `/record`,

	/**
	 * Gets current user's AI usage dashboard
	 * @method GET
	 * @returns UserAIDashboardDto
	 */
	GET_USER_DASHBOARD: `/dashboard`,

	/**
	 * Gets current user's daily usage for a date range
	 * @method GET
	 * @query {string} startDate - Start date (optional)
	 * @query {string} endDate - End date (optional)
	 * @returns DailyUsageDto[]
	 */
	GET_USER_DAILY_USAGE: `/daily-usage`,

	// ============================
	// SERVICE MANAGEMENT ENDPOINTS (PUBLIC)
	// ============================

	/**
	 * Gets all available AI services
	 * @method GET
	 * @query {boolean} includeInactive - Include inactive services (default: false)
	 * @returns AIServiceStatusDto[]
	 */
	GET_SERVICES: `/services`,

	/**
	 * Gets specific AI service by key
	 * @method GET
	 * @param {string} serviceKey - The service key
	 * @returns AIServiceStatusDto
	 */
	GET_SERVICE_BY_KEY: `/services/:serviceKey`,

	// ============================
	// PLAN MANAGEMENT ENDPOINTS (PUBLIC)
	// ============================

	/**
	 * Gets all available user plans
	 * @method GET
	 * @query {boolean} includeInactive - Include inactive plans (default: false)
	 * @returns UserPlanDto[]
	 */
	GET_PLANS: `/plans`,

	/**
	 * Gets specific user plan by ID
	 * @method GET
	 * @param {string} planId - The plan ID (GUID)
	 * @returns UserPlanDto
	 */
	GET_PLAN_BY_ID: `/plans/:planId`,

	// ============================
	// USER SUBSCRIPTION MANAGEMENT
	// ============================

	/**
	 * Gets current user's active subscription
	 * @method GET
	 * @returns UserSubscriptionDto
	 */
	GET_CURRENT_SUBSCRIPTION: `/subscription/current`,

	/**
	 * Gets current user's subscription history
	 * @method GET
	 * @query {boolean} includeInactive - Include inactive subscriptions (default: true)
	 * @returns UserSubscriptionDto[]
	 */
	GET_SUBSCRIPTION_HISTORY: `/subscription/history`,

	// ============================
	// TRIAL MANAGEMENT ENDPOINTS
	// ============================

	/**
	 * Starts a trial subscription for the current user
	 * @method POST
	 * @param {string} planId - The plan ID (GUID)
	 * @returns UserSubscriptionDto
	 */
	START_TRIAL: `/subscription/trial/:planId`,

	/**
	 * Converts trial to paid subscription
	 * @method POST
	 * @param {string} subscriptionId - The subscription ID (GUID)
	 * @body ConvertTrialRequest
	 * @returns UserSubscriptionDto
	 */
	CONVERT_TRIAL_TO_PAID: `/subscription/:subscriptionId/convert`,

	/**
	 * Checks if user can start a trial for a specific plan
	 * @method GET
	 * @param {string} planId - The plan ID (GUID)
	 * @returns TrialEligibilityDto
	 */
	CHECK_TRIAL_ELIGIBILITY: `/subscription/trial/:planId/eligibility`,

	// ============================
	// ADMIN SERVICE MANAGEMENT ENDPOINTS
	// ============================

	/**
	 * Creates a new AI service (Admin only)
	 * @method POST
	 * @body CreateUpdateAIServiceRequestDto
	 * @returns AIServiceStatusDto
	 */
	ADMIN_CREATE_SERVICE: `/admin/services`,

	/**
	 * Updates an existing AI service (Admin only)
	 * @method PUT
	 * @param {string} serviceId - The service ID (GUID)
	 * @body CreateUpdateAIServiceRequestDto
	 * @returns AIServiceStatusDto
	 */
	ADMIN_UPDATE_SERVICE: `/admin/services/:serviceId`,

	/**
	 * Deletes an AI service (Admin only)
	 * @method DELETE
	 * @param {string} serviceId - The service ID (GUID)
	 */
	ADMIN_DELETE_SERVICE: `/admin/services/:serviceId`,

	/**
	 * Sets AI service status (Admin only)
	 * @method PATCH
	 * @param {string} serviceId - The service ID (GUID)
	 * @body SetServiceStatusRequest
	 * @returns AIServiceStatusDto
	 */
	ADMIN_SET_SERVICE_STATUS: `/admin/services/:serviceId/status`,

	// ============================
	// ADMIN PLAN MANAGEMENT ENDPOINTS
	// ============================

	/**
	 * Creates a new user plan (Admin only)
	 * @method POST
	 * @body CreateUpdateUserPlanRequestDto
	 * @returns UserPlanDto
	 */
	ADMIN_CREATE_PLAN: `/admin/plans`,

	/**
	 * Duplicates a plan and its usage limits (Admin only)
	 * @method POST
	 * @param {string} planId - The source plan ID (GUID)
	 * @body DuplicateUserPlanRequestDto
	 * @returns UserPlanDto
	 */
	ADMIN_DUPLICATE_PLAN: `/admin/plans/:planId/duplicate`,

	/**
	 * Updates an existing user plan (Admin only)
	 * @method PUT
	 * @param {string} planId - The plan ID (GUID)
	 * @body CreateUpdateUserPlanRequestDto
	 * @returns UserPlanDto
	 */
	ADMIN_UPDATE_PLAN: `/admin/plans/:planId`,

	/**
	 * Deletes a user plan (Admin only)
	 * @method DELETE
	 * @param {string} planId - The plan ID (GUID)
	 */
	ADMIN_DELETE_PLAN: `/admin/plans/:planId`,

	/**
	 * Sets usage limits for a plan and service (Admin only)
	 * @method POST
	 * @body SetUsageLimitRequestDto
	 */
	ADMIN_SET_USAGE_LIMIT: `/admin/plans/usage-limits`,

	/**
	 * Gets usage limits for a specific plan (Admin only)
	 * @method GET
	 * @param {string} planId - The plan ID (GUID)
	 * @returns UsageLimitDto[]
	 */
	ADMIN_GET_PLAN_USAGE_LIMITS: `/admin/plans/:planId/usage-limits`,

	// ============================
	// PAYMENT GATEWAY PLAN MANAGEMENT ENDPOINTS
	// ============================

	/**
	 * Gets all payment gateway plans for a specific plan
	 * @method GET
	 * @param {string} planId - The plan ID (GUID)
	 * @query {boolean} includeInactive - Include inactive gateway plans (default: false)
	 * @returns PaymentGatewayPlanDto[]
	 */
	GET_PLAN_GATEWAY_PLANS: `/plans/:planId/gateway-plans`,

	/**
	 * Gets a specific payment gateway plan by ID
	 * @method GET
	 * @param {string} gatewayPlanId - The gateway plan ID (GUID)
	 * @returns PaymentGatewayPlanDto
	 */
	GET_GATEWAY_PLAN_BY_ID: `/gateway-plans/:gatewayPlanId`,

	/**
	 * Gets payment gateway plan for specific plan, gateway and region
	 * @method GET
	 * @param {string} planId - The plan ID (GUID)
	 * @param {PaymentGateway} gateway - The payment gateway
	 * @param {PaymentRegion} region - The payment region
	 * @returns PaymentGatewayPlanDto
	 */
	GET_SPECIFIC_GATEWAY_PLAN: `/plans/:planId/gateway-plans/:gateway/:region`,

	/**
	 * Creates a new payment gateway plan (Admin only)
	 * @method POST
	 * @body CreateUpdatePaymentGatewayPlanRequestDto
	 * @returns PaymentGatewayPlanDto
	 */
	ADMIN_CREATE_GATEWAY_PLAN: `/admin/gateway-plans`,

	/**
	 * Updates an existing payment gateway plan (Admin only)
	 * @method PUT
	 * @param {string} gatewayPlanId - The gateway plan ID (GUID)
	 * @body CreateUpdatePaymentGatewayPlanRequestDto
	 * @returns PaymentGatewayPlanDto
	 */
	ADMIN_UPDATE_GATEWAY_PLAN: `/admin/gateway-plans/:gatewayPlanId`,

	/**
	 * Deletes a payment gateway plan (Admin only)
	 * @method DELETE
	 * @param {string} gatewayPlanId - The gateway plan ID (GUID)
	 */
	ADMIN_DELETE_GATEWAY_PLAN: `/admin/gateway-plans/:gatewayPlanId`,

	// ============================
	// ADMIN USER SUBSCRIPTION MANAGEMENT
	// ============================

	/**
	 * Assigns a plan to a user (Admin only)
	 * @method POST
	 * @body AssignUserPlanRequestDto
	 * @returns UserSubscriptionDto
	 */
	ADMIN_ASSIGN_USER_PLAN: `/admin/subscriptions`,

	/**
	 * Gets user subscriptions (Admin only)
	 * @method GET
	 * @query {string} userId - User ID to filter by (optional)
	 * @query {boolean} includeInactive - Include inactive subscriptions (optional)
	 * @returns UserSubscriptionDto[]
	 */
	ADMIN_GET_USER_SUBSCRIPTIONS: `admin/users/:userId/subscriptions`,

	// ============================
	// ADMIN STATISTICS AND ANALYTICS
	// ============================

	/**
	 * Gets usage statistics (Admin only)
	 * @method POST
	 * @body UsageStatisticsRequestDto
	 * @returns UsageStatisticsResponseDto
	 */
	ADMIN_GET_USAGE_STATISTICS: `/admin/statistics`,

	/**
	 * Gets system health metrics (Admin only)
	 * @method GET
	 * @returns SystemHealthDto
	 */
	ADMIN_GET_SYSTEM_HEALTH: `/admin/analytics/system-health`,

	// ============================
	// ADMIN SYSTEM MANAGEMENT
	// ============================

	/**
	 * Gets system settings (Admin only)
	 * @method GET
	 * @returns Dictionary<string, string>
	 */
	ADMIN_GET_SYSTEM_SETTINGS: `/admin/system/settings`,

	/**
	 * Updates a system setting (Admin only)
	 * @method PUT
	 * @param {string} key - Setting key
	 * @body UpdateSystemSettingRequest
	 */
	ADMIN_UPDATE_SYSTEM_SETTING: `/admin/system/settings/:key`,

	/**
	 * Refreshes system caches (Admin only)
	 * @method POST
	 */
	ADMIN_REFRESH_CACHES: `/admin/system/refresh-caches`,

	/**
	 * Forces cleanup of expired data (Admin only)
	 * @method POST
	 */
	ADMIN_CLEANUP_EXPIRED_DATA: `/admin/system/cleanup-expired`,
};

export const RAZORPAY_ENDPOINTS = {
	/**
	 * The base URL for the Razorpay API.
	 */
	CONTROLLER: "api/Razorpay",

	/**
	 * Creates a Razorpay payment order or subscription for subscribing to a plan.
	 * @method POST
	 * @body CreateSubscriptionPaymentRequestDto
	 * @returns CreateSubscriptionPaymentResponseDto
	 */
	CREATE_ORDER: "/create-order",

	/**
	 * Verifies a completed payment and activates the subscription.
	 * @method POST
	 * @body VerifyPaymentRequestDto
	 * @returns VerifyPaymentResponseDto
	 */
	VERIFY_PAYMENT: "/verify-payment",

	/**
	 * Gets payment history for the current user.
	 * @method GET
	 * @query includeFailedPayments - Whether to include failed payments in history
	 * @returns PaymentHistoryDto
	 */
	GET_PAYMENT_HISTORY: "/payment-history",

	/**
	 * Gets details of a specific payment transaction.
	 * @method GET
	 * @param {string} transactionId - Transaction ID
	 * @returns PaymentTransactionDto
	 */
	GET_TRANSACTION: "/transaction/:transactionId",

	/**
	 * Cancels a Razorpay subscription (stops auto-renewal).
	 * @method POST
	 * @param {string} subscriptionId - Subscription ID
	 * @body CancelSubscriptionRequestDto
	 * @returns Success message
	 */
	CANCEL_SUBSCRIPTION: "/subscription/:subscriptionId:guid/cancel",

	/**
	 * Pauses a Razorpay subscription.
	 * @method POST
	 * @param {string} subscriptionId - Subscription ID
	 * @body PauseSubscriptionRequestDto
	 * @returns Success message
	 */
	PAUSE_SUBSCRIPTION: "/subscription/:subscriptionId:guid/pause",

	/**
	 * Resumes a paused Razorpay subscription.
	 * @method POST
	 * @param {string} subscriptionId - Subscription ID
	 * @returns Success message
	 */
	RESUME_SUBSCRIPTION: "/subscription/:subscriptionId:guid/resume",

	/**
	 * Gets current subscription details.
	 * @method GET
	 * @param {string} razorpaySubscriptionId - Razorpay Subscription ID
	 * @returns UserSubscriptionDto
	 */
	GET_SUBSCRIPTION: "/subscription/:razorpaySubscriptionId/details",

	/**
	 * Gets Razorpay key ID for client-side integration.
	 * @method GET
	 * @returns { keyId: string }
	 */
	GET_RAZORPAY_KEY: "/razorpay-key",
};

export const DELETION_REQUEST_ENDPOINTS = {
	/**
	 * The base URL for the deletion request API.
	 */
	CONTROLLER: "api/DeletionRequest",

	/**
	 * Create a new account deletion request with a 7-day grace period.
	 * @method POST
	 * @body CreateDeletionRequestDTO
	 * @returns DeletionRequestResponseDTO
	 */
	REQUEST_DELETION: "/request-deletion",

	/**
	 * Check if the current user has an active deletion request.
	 * @method GET
	 * @returns CheckDeletionStatusDTO
	 */
	CHECK_STATUS: "/check-status",

	/**
	 * Revoke/cancel an active deletion request.
	 * @method POST
	 * @returns { success: boolean, message: string }
	 */
	REVOKE_DELETION: "/revoke-deletion",

	/**
	 * Get all deletion requests (admin only).
	 * @method GET
	 * @returns { success: boolean, count: number, data: DeletionRequestResponseDTO[] }
	 */
	GET_ALL_DELETION_REQUESTS: "/admin/all-deletion-requests",

	/**
	 * Get a specific deletion request by ID (admin only).
	 * @method GET
	 * @param {string} deletionRequestId - The deletion request ID.
	 * @returns { success: boolean, data: DeletionRequestResponseDTO }
	 */
	GET_DELETION_REQUEST: "/admin/deletion-request/:deletionRequestId",

	/**
	 * Manually trigger processing of due deletion requests (admin only).
	 * @method POST
	 * @returns { success: boolean, message: string, processedCount: number }
	 */
	PROCESS_DUE_DELETIONS: "/admin/process-due-deletions",
};

export const ADMIN_COURSE_EMBEDDING_ENDPOINTS = {
	/**
	 * The base URL for the admin course embedding API.
	 */
	CONTROLLER: "api/AdminCourseEmbedding",

	/**
	 * Triggers the embedding generation manually.
	 * @method POST
	 */
	TRIGGER_EMBEDDING_GENERATION: `/trigger-embedding-generation`,
	REBUILD_EMBEDDINGS: `/rebuild-embeddings`,
};

export const ADMIN_JOB_PROFILE_MATCH_ENDPOINTS = {
	CONTROLLER: "api/AdminJobProfileMatch",
	BACKFILL: "/backfill",
	BACKFILL_STATUS: "/backfill/status",
};

/** Private, per-user job-to-professional-profile fit results. */
export const JOB_PROFILE_MATCH_ENDPOINTS = {
	CONTROLLER: "api/JobProfileMatch",
	GET_BY_JOB_ID: "/:jobId",
};

export const EXTENSION_ANALYTICS_ENDPOINTS = {
	/**
	 * The base URL for extension analytics APIs.
	 */
	CONTROLLER: "api/ExtensionAnalytics",

	/**
	 * Fetches failed extension URLs for admins.
	 * @method GET
	 */
	GET_FAILED_URLS: `/failed-urls`,

	/**
	 * Deletes one failed URL record.
	 * @method DELETE
	 */
	DELETE_FAILED_URL: `/failed-urls/:id`,

	/**
	 * Resolves one failed URL record.
	 * @method PUT
	 */
	RESOLVE_FAILED_URL: `/failed-urls/:id/resolve`,

	/**
	 * Fetches aggregate extension analytics.
	 * @method GET
	 */
	GET_ANALYTICS: `/analytics`,
};

export const LINKEDIN_ENDPOINTS = {
	/**
	 * The base URL for the LinkedIn API.
	 */
	CONTROLLER: "api/LinkedInAnalyzer",

	/**
	 * Gets the LinkedIn profile optimization result for the current user.
	 * @method GET
	 * @returns { LinkedInOptimizationResponseDto }
	 */
	GET_OPTIMIZATION: "/",

	/**
	 * Updates the LinkedIn optimization result for the current user.
	 * @method PUT
	 * @body { result: string }
	 * @returns { LinkedInOptimizationResponseDto }
	 */
	UPDATE_OPTIMIZATION: "/",
};

export const ADMIN_CRON_JOB_ENDPOINTS = {
	/**
	 * The base controller URL for cron job management.
	 */
	CONTROLLER: "api/AdminCronJob",

	/**
	 * Returns all registered cron jobs with their current status and last execution details.
	 * @method GET
	 * @returns { CronJobStatusDto[] }
	 */
	GET_ALL: "/",

	/**
	 * Manually triggers a cron job by its class name.
	 * @method POST
	 * @param {string} name - The cron job class name.
	 * @returns { message: string }
	 */
	TRIGGER: "/:name/trigger",

	/**
	 * Triggers a full backfill of all user dashboards.
	 * @method POST
	 * @returns { message: string }
	 */
	BACKFILL_DASHBOARDS: "/backfill-dashboards",
};

/** Partner-issued access codes and university email verification. */
export const PARTNER_ACCESS_ENDPOINTS = {
	CONTROLLER: "api/partner-access",
	ADMIN_ORGANIZATIONS: "/admin/organizations",
	ADMIN_ORGANIZATION_BY_ID: "/admin/organizations/:organizationId",
	ADMIN_CODES: "/admin/codes",
	ADMIN_CODE_BY_ID: "/admin/codes/:codeId",
	ADMIN_GRANTS: "/admin/grants",
	ADMIN_BANS: "/admin/bans",
	REDEEM: "/redeem",
	UNIVERSITY_REQUEST_VERIFICATION: "/university/request-verification",
	UNIVERSITY_CONFIRM_VERIFICATION: "/university/confirm-verification",
	UNIVERSITIES: "/universities",
};
